// Global state for the LLM chat assistant
// Keeps the assistant connection and message history alive across widget open/close cycles

"use strict";

import { AppEvents } from "./app-events";
import { AuthController } from "./auth";
import {
    globalLLmGetContext,
    SidecarLLmChatAssistant,
    SidecarLLmChatAssistantEventHandlerState,
    SidecarLLmChatAssistantMessage,
} from "./llm-chat-assitant";
import { getUniqueStringId } from "@/utils/unique-id";
import { i18nData } from "@/i18n";

const MAX_MESSAGES = 256;

/**
 * Listener for global chat assistant state changes
 */
export interface GlobalChatAssistantListener {
    onStateChanged(state: SidecarLLmChatAssistantEventHandlerState): void;
    onMessagesChanged(): void;
}

// Global singleton state
let globalAssistant: SidecarLLmChatAssistant | null = null;
let globalStatus: SidecarLLmChatAssistantEventHandlerState = "loading";
let globalMessages: SidecarLLmChatAssistantMessage[] = [];
let globalIsBeginning = true;
let globalInitialized = false;

// Set of listeners
const listeners = new Set<GlobalChatAssistantListener>();

/**
 * Registers a listener for global chat assistant state changes
 */
export function globalChatAssistantAddListener(listener: GlobalChatAssistantListener) {
    listeners.add(listener);
}

/**
 * Removes a listener
 */
export function globalChatAssistantRemoveListener(listener: GlobalChatAssistantListener) {
    listeners.delete(listener);
}

/**
 * Gets the current messages
 */
export function globalChatAssistantGetMessages(): SidecarLLmChatAssistantMessage[] {
    return globalMessages;
}

/**
 * Gets the current status
 */
export function globalChatAssistantGetStatus(): SidecarLLmChatAssistantEventHandlerState {
    return globalStatus;
}

/**
 * Sends a chat message
 */
export function globalChatAssistantSend(message: string) {
    if (!globalAssistant || !message) {
        return;
    }

    globalMessages.push({
        id: getUniqueStringId(),
        role: "user",
        content: message,
    });

    truncateMessages();
    notifyMessagesChanged();

    globalAssistant.chat(message);
}

/**
 * Clears the chat history
 */
export function globalChatAssistantClear() {
    if (!globalAssistant) {
        return;
    }

    globalMessages = [];
    globalAssistant.clear();
    notifyMessagesChanged();
}

/**
 * Cancels the current generation
 */
export function globalChatAssistantCancel() {
    if (!globalAssistant) {
        return;
    }

    globalAssistant.cancel();
}

// Internal helpers

function getTranslatedHello(): string {
    return i18nData.messages.get("Hello") || "Hello";
}

function truncateMessages() {
    while (globalMessages.length > MAX_MESSAGES) {
        globalMessages.shift();
    }
}

function notifyStateChanged() {
    for (const listener of listeners) {
        listener.onStateChanged(globalStatus);
    }
}

function notifyMessagesChanged() {
    for (const listener of listeners) {
        listener.onMessagesChanged();
    }
}

function updateLLmContext() {
    if (!globalAssistant) {
        return;
    }

    const [contextId, extraContext, tools] = globalLLmGetContext();
    globalAssistant.setContext(contextId, extraContext);
    globalAssistant.setTools(tools);
}

function createAssistant() {
    if (globalAssistant) {
        return;
    }

    globalIsBeginning = true;

    globalAssistant = new SidecarLLmChatAssistant(
        {
            onStateChanged: (state) => {
                globalStatus = state;

                if (state === "idle" && globalIsBeginning) {
                    globalIsBeginning = false;
                    globalAssistant.chat(getTranslatedHello());
                }

                notifyStateChanged();
            },
            onError: (code, msg) => {
                globalMessages.push({
                    id: getUniqueStringId(),
                    role: "error",
                    content: code + ": " + msg,
                });

                notifyMessagesChanged();
            },
            onMessage: (id, chunk) => {
                const message = globalMessages.find((msg) => msg.id === id);

                if (message) {
                    message.content += chunk;
                } else {
                    globalMessages.push({
                        id,
                        role: "assistant",
                        content: chunk,
                    });

                    truncateMessages();
                }

                notifyMessagesChanged();
            },
            onReconnected: () => {
                globalMessages.push({
                    id: getUniqueStringId(),
                    role: "success",
                    content: "Connection re-established successfully.",
                });

                notifyMessagesChanged();

                setTimeout(() => {
                    globalMessages = globalMessages.filter((m) => m.role !== "error");
                    notifyMessagesChanged();
                }, 3000);

                setTimeout(() => {
                    globalMessages = globalMessages.filter((m) => m.role !== "success");
                    notifyMessagesChanged();
                }, 7000);
            },
        },
        [],
    );

    updateLLmContext();
}

function destroyAssistant() {
    if (!globalAssistant) {
        return;
    }

    globalAssistant.dispose();
    globalAssistant = null;
    globalStatus = "loading";
    globalMessages = [];
    globalIsBeginning = true;
    notifyStateChanged();
    notifyMessagesChanged();
}

function checkPermissionsAndManageAssistant() {
    const canUseLLm = AuthController.isAuthenticated() && AuthController.hasPermission("llm.use") && AuthController.SidecarLLM.assistant;

    if (canUseLLm) {
        if (!globalAssistant) {
            createAssistant();
        }
    } else {
        destroyAssistant();
    }
}

/**
 * Initializes the global chat assistant.
 * Should be called once at app startup.
 */
export function globalChatAssistantInit() {
    if (globalInitialized) {
        return;
    }

    globalInitialized = true;

    AppEvents.AddEventListener("auth-status-changed", () => {
        checkPermissionsAndManageAssistant();
    });

    AppEvents.AddEventListener("llm-context-changed", () => {
        updateLLmContext();
    });

    checkPermissionsAndManageAssistant();
}
